/* Same-origin Shopify app proxy; never send customer details to an arbitrary URL. */
/* global UcgRegistrationPhone, UcgRegistrationLead */
(() => {
  const config = document.querySelector('[data-ucg-registration-sync]');
  const form = document.querySelector('[data-registration-account-form]');
  if (!config || !form || config.dataset.designMode === 'true') return;
  const popup = form.closest('professional-registration-popup');
  const signedIn = config.dataset.signedIn === 'true';
  const endpoint = '/apps/ucg-registration';
  const storageKey = 'ucg-professional-registration-v1';
  const ttl = 30 * 60 * 1000;
  const selectors = {
    first_name: '[autocomplete="given-name"]', last_name: '[autocomplete="family-name"]',
    email: '[autocomplete="email"]', phone: '[autocomplete="tel-national"]',
    country: '[aria-label="Country calling code"]', business_practice_name: '[autocomplete="organization"]',
    professional_type: '[data-professional-type]', referral_source: '[data-referral-select]',
    referral_other: '[data-referral-other] input',
  };
  const fields = Object.fromEntries(Object.entries(selectors).map(([key, selector]) => [key, form.querySelector(selector)]));
  if (Object.values(fields).some(field => !field)) return;
  const status = document.createElement('p');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.setAttribute('tabindex', '-1');
  status.hidden = true;
  status.style.cssText = 'padding:14px;margin:12px 0;line-height:1.5;border:1px solid #b18a38;border-radius:6px;background:#fff8e8;color:#263044';
  form.insertBefore(status, form.querySelector('.professional-registration-popup__actions'));
  const phoneFeedback = document.createElement('small');
  phoneFeedback.id = 'ucg-registration-phone-feedback';
  phoneFeedback.style.cssText = 'display:block;font-size:14px;line-height:1.4;font-weight:400;color:#263044';
  const phoneHelp = 'Spaces, brackets, dashes and pasted country codes are accepted.';
  phoneFeedback.textContent = phoneHelp;
  fields.phone.closest('label').append(phoneFeedback);
  fields.phone.setAttribute('aria-describedby', phoneFeedback.id);
  const grid = form.querySelector('.professional-registration-popup__grid');
  if (grid) grid.style.alignItems = 'start';
  // Handle native validation here so every submit also gets visible feedback.
  form.noValidate = true;
  function showStatus(message, error = false) {
    status.hidden = false;
    status.textContent = message;
    status.setAttribute('role', error ? 'alert' : 'status');
    status.style.borderColor = error ? '#b42318' : '#b18a38';
    status.style.color = error ? '#9b1c14' : '#263044';
    status.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    if (error) status.focus({ preventScroll: true });
  }
  let saving = false;
  let errorNotice;
  function openForm(review = false) {
    customElements.whenDefined('professional-registration-popup').then(() => popup.open?.(undefined, review));
  }
  function showError(message) {
    showStatus(message, true);
    errorNotice?.remove();
    errorNotice = document.createElement('aside');
    errorNotice.setAttribute('role', 'alert');
    errorNotice.style.cssText = 'position:fixed;top:12px;left:12px;right:12px;z-index:10001;padding:16px;background:#fff8e8;color:#263044;border:1px solid #b18a38';
    errorNotice.textContent = message;
    const retry = document.createElement('button');
    retry.type = 'button'; retry.textContent = 'Review registration details'; retry.style.marginLeft = '12px';
    retry.addEventListener('click', () => openForm(true));
    errorNotice.append(retry); document.body.append(errorNotice);
    if (!signedIn) openForm();
  }
  function values() { return Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, field.value.trim()])); }
  function restore(data) {
    for (const [key, field] of Object.entries(fields)) if (typeof data[key] === 'string') field.value = data[key];
    popup.updateReferralOther?.();
  }
  function validatePhone(data) {
    try {
      if (typeof UcgRegistrationPhone === 'undefined') {
        throw new Error('Phone validation could not load. Refresh this page before continuing.');
      }
      return { ...data, phone: UcgRegistrationPhone.normalize(data.phone, data.country) };
    } catch (error) {
      showError(error.message);
      phoneFeedback.textContent = error.message;
      phoneFeedback.style.color = '#9b1c14';
      fields.phone.setAttribute('aria-invalid', 'true');
      fields.phone.focus({ preventScroll: true });
      return null;
    }
  }
  function resetPhoneFeedback() {
    fields.phone.removeAttribute('aria-invalid');
    phoneFeedback.textContent = phoneHelp;
    phoneFeedback.style.color = '#263044';
  }
  fields.phone.addEventListener('input', resetPhoneFeedback);
  fields.country.addEventListener('change', resetPhoneFeedback);
  async function responseJson(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data || typeof data !== 'object') throw new Error(data.error || 'We could not save your details. Please retry or contact support.');
    return data;
  }
  async function save(data, receipt) {
    if (saving) return;
    data = validatePhone(data);
    if (!data) return;
    fields.phone.removeAttribute('aria-invalid');
    saving = true;
    const submit = form.querySelector('[type="submit"]');
    const submitLabel = submit?.textContent;
    if (submit) { submit.disabled = true; submit.textContent = 'Saving…'; }
    errorNotice?.remove(); errorNotice = undefined;
    showStatus(receipt ? 'Linking your saved registration to Shopify…' : 'Saving your registration details…');
    try {
      const challenge = await responseJson(await fetch(endpoint, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000) }));
      if (typeof challenge.token !== 'string') throw new Error('Registration saving is not available yet. Please contact support.');
      const result = await responseJson(await fetch(endpoint, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: challenge.token, registration: data, receipt }),
        signal: AbortSignal.timeout(30000),
      }));
      if (result.saved !== true) throw new Error('The save was not confirmed. Please retry.');
      errorNotice?.remove(); errorNotice = undefined;
      try { sessionStorage.removeItem(storageKey); } catch { /* Saving succeeded even if storage is unavailable. */ }
      showStatus(receipt ? 'Registration saved in UCG Shopify and linked to your Shopify account.' : 'Your registration details have been saved.');
      const notice = document.createElement('p');
      notice.setAttribute('role', 'status');
      notice.style.cssText = 'position:fixed;bottom:24px;left:24px;right:24px;z-index:10000;padding:16px;background:#fff8e8;color:#263044;border:1px solid #b18a38';
      notice.textContent = status.textContent;
      const dismiss = document.createElement('button');
      dismiss.type = 'button'; dismiss.textContent = 'Dismiss'; dismiss.style.marginLeft = '16px';
      dismiss.addEventListener('click', () => notice.remove()); notice.append(dismiss); document.body.append(notice);
      popup.close?.();
    } catch (error) {
      const message = error instanceof Error && error.name !== 'TimeoutError' ? error.message : 'Saving took too long. Please retry; your details are still in the form.';
      showError(receipt ? `Your registration is saved in UCG Shopify, but Shopify account linking needs attention. ${message}` : message);
    } finally { saving = false; if (submit) { submit.disabled = false; submit.textContent = submitLabel; } }
  }
  function confirmShopifyLogin() {
    const dialog = document.querySelector('[data-registration-redirect-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') {
      // Keep the notice available if an older cached template is still being served.
      return Promise.resolve(window.confirm('Your registration details have been saved. Continue to Shopify login to verify your email and finish setting up your account? Use the same email address you entered in this form.'));
    }
    return new Promise(resolve => {
      dialog.returnValue = '';
      dialog.addEventListener('close', () => resolve(dialog.returnValue === 'continue'), { once: true });
      dialog.showModal();
    });
  }
  async function submitRegistration(data) {
    if (saving) return;
    saving = true;
    const submit = form.querySelector('[type="submit"]');
    const label = submit?.textContent;
    if (submit) { submit.disabled = true; submit.textContent = 'Saving registration…'; }
    errorNotice?.remove(); errorNotice = undefined;
    showStatus('Saving your registration in UCG Shopify…');
    let registrationSaved = false;
    try {
      let pending;
      try {
        const previous = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        const receipt = previous?.receipt && JSON.stringify(previous.data) === JSON.stringify(data)
          ? previous.receipt : { id: crypto.randomUUID(), key: crypto.randomUUID() };
        pending = { created: Date.now(), data, receipt, leadSent: previous?.receipt?.id === receipt.id && previous.leadSent === true };
        // Persist the receipt before requesting a write, so a timeout can retry the same record.
        sessionStorage.setItem(storageKey, JSON.stringify(pending));
      } catch { throw new Error('Allow session storage in this browser to save your registration before sign-in.'); }
      if (typeof UcgRegistrationLead === 'undefined') throw new Error('Registration verification could not load. Refresh the page before continuing.');
      const captchaToken = pending.leadSent ? '' : await UcgRegistrationLead.token();
      const challenge = await responseJson(await fetch(endpoint, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000) }));
      if (typeof challenge.token !== 'string') throw new Error('Registration saving is not available yet. Please contact support.');
      const result = await responseJson(await fetch(endpoint, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'submit', token: challenge.token, receipt: pending.receipt, registration: data }),
        signal: AbortSignal.timeout(30000),
      }));
      if (result.saved !== true || result.registration_id !== pending.receipt.id) {
        throw new Error('UCG Shopify did not confirm your registration. Please retry before signing in.');
      }
      registrationSaved = true;
      if (!pending.leadSent) {
        showStatus('Registration saved. Sending your professional pricing request…');
        await UcgRegistrationLead.submit(data, pending.receipt.id, captchaToken, fields.country.selectedOptions?.[0]?.textContent);
        pending.leadSent = true;
        sessionStorage.setItem(storageKey, JSON.stringify(pending));
      }
      if (signedIn) { saving = false; await save(data, pending.receipt); return; }
      const destination = new URL(form.dataset.loginUrl || form.action, location.origin);
      destination.searchParams.set('login_hint', data.email);
      showStatus('Your registration is saved. Continue to Shopify login to verify your email and finish setting up your account.');
      if (!(await confirmShopifyLogin())) return;
      popup.rememberDismissal?.();
      showStatus('Registration saved in UCG Shopify. Opening Shopify sign-in to verify and link your account…');
      if (submit) submit.textContent = 'Registration saved — opening sign-in…';
      location.assign(destination.href);
    } catch (error) {
      const message = error instanceof Error && error.name !== 'TimeoutError' ? error.message : 'The request took too long. Please retry; you have not been sent to login.';
      showError(registrationSaved ? `Your registration is saved, but the lead notification request needs attention. ${message}` : message);
    } finally { saving = false; if (submit) { submit.disabled = false; submit.textContent = label; } }
  }
  // Capture before the existing popup's redirect handler.
  document.addEventListener('submit', event => {
    if (event.target !== form) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (saving) return;
    if (!form.reportValidity()) {
      const invalid = form.querySelector(':invalid');
      const key = Object.keys(fields).find(key => fields[key] === invalid);
      const label = key ? key.replaceAll('_', ' ') : 'required fields';
      showError(`Please check ${label}. ${invalid?.validationMessage || 'Complete all required fields before continuing.'}`);
      return;
    }
    const data = validatePhone(values());
    if (!data) return;
    fields.phone.removeAttribute('aria-invalid');
    void submitRegistration(data);
  }, true);
  if (!signedIn) return;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return;
    const pending = JSON.parse(raw);
    if (!pending.data || !Number.isFinite(pending.created) || Date.now() - pending.created > ttl || pending.created > Date.now()) {
      sessionStorage.removeItem(storageKey);
      showError('Your registration form expired. Please enter your details again to save them.'); return;
    }
    restore(pending.data);
    // Wait until the popup custom element is ready before showing errors or restoring conditional fields.
    customElements.whenDefined('professional-registration-popup').then(() => {
      restore(pending.data); void save(pending.data, pending.receipt);
    });
  } catch { try { sessionStorage.removeItem(storageKey); } catch {} }
})();
